import {
  Component, AfterViewInit, ViewChild, ElementRef,
  Input, OnChanges, SimpleChanges, OnDestroy,
  Output, EventEmitter, HostListener, ChangeDetectorRef
} from '@angular/core';
import nextbillion, {
  Map, Marker, LngLatBounds, Popup,
  FullscreenControl, NavigationControl, ScaleControl, GeolocateControl
} from '@nbai/nbmap-gl';

import { NextBillionMarker } from '../models/response/appointment/roadshow-timeslot-main-response-model';
import { NavigationData }    from 'src/app/admin/servicies/route-service.service';
import { MapService }        from 'src/app/services/map.service';
import { environment }       from 'src/environments/environment';

import { parseLatLng, filterValidCoords, buildBounds, isBoundsValid } from './map.coords.helper';
import { resolveMarkerAssets }                                         from './map-assets.helper'
import { MapStyleType, mountStyleOverlay, syncOverlayButtons, applyFullscreenDimensions } from './map-overlay.helper';

type StyleType = MapStyleType;

@Component({
  selector:    'app-map',
  templateUrl: './map.component.html',
  styleUrls:   ['./map.component.sass'],
  standalone:  false
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input()  points: NextBillionMarker[] = [];
  @Input() height: string | number = 400;

  @Output() navigationData = new EventEmitter<NavigationData>();
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  @ViewChild('mapWrapper')   mapWrapper!: ElementRef;

  // ── Public state (template bindings) ─────────────────────────────────────
  public isLoading       = false;
  public currentMapStyle: StyleType = 'streets';

  public get containerHeight(): string {
    if (typeof this.height === 'number') return `${this.height}px`;
    return this.height;
  }

  // ── Private map state ─────────────────────────────────────────────────────
  private map!: Map;
  private markers: Marker[] = [];
  private currentRouteCoordinates: [number, number][] = [];
  private isMapLoaded       = false;
  private pendingRouteRender = false;

  // ── Loading counters ──────────────────────────────────────────────────────
  private routesProcessed      = 0;
  private totalRoutesToProcess = 0;
  private markersAdded         = false;

  // ── Async handles ─────────────────────────────────────────────────────────
  private routeRetryHandle:   ReturnType<typeof requestAnimationFrame> | null = null;
  private styleTimeoutHandle: ReturnType<typeof setTimeout>            | null = null;

  private readonly MAX_RETRY_ATTEMPTS = 8;
  private readonly RETRY_DELAY_MS     = 300;

  private resizeObserver: ResizeObserver | null = null;

  // ── Fullscreen ────────────────────────────────────────────────────────────
  private fullscreenOverlay: HTMLElement | null = null;
  private readonly onFullscreenChange = () => this.handleFullscreenChange();

  // ── Style URLs ────────────────────────────────────────────────────────────
  readonly apiKey = environment.nextBillionApiKey;

  private readonly mapStyleUrls: Record<StyleType, string> = {
    streets:   `https://api.nextbillion.io/maps/streets/style.json?key=${this.apiKey}`,
    satellite: `https://api.nextbillion.io/maps/satellite/style.json?key=${this.apiKey}`,
    hybrid:    `https://api.nextbillion.io/maps/hybrid/style.json?key=${this.apiKey}`,
  };

  constructor(
    private readonly mapService: MapService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.initializeMap();
      this.initResizeObserver();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['points'] && !changes['points'].isFirstChange()) {
      this.setLoadingState(true);
      this.resetCounters();
      if (this.map && this.isMapLoaded) {
        this.drawRouteWithMarkers(this.points);
      } else if (this.map) {
        this.map.once('load', () => this.drawRouteWithMarkers(this.points));
      }
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingRetry();
    if (this.styleTimeoutHandle) clearTimeout(this.styleTimeoutHandle);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.clearMarkers();
    this.unmountFullscreenOverlay();

    document.removeEventListener('fullscreenchange',       this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);

    if (this.map) {
      this.clearRoute();
      this.map.remove();
    }
  }

  // ─── Map Initialisation ───────────────────────────────────────────────────

  private initializeMap(): void {
    this.setLoadingState(true);
    nextbillion.setApiKey(this.apiKey);

    this.map = new Map({
      container:          this.mapContainer.nativeElement,
      style:              this.mapStyleUrls.streets,
      center:             { lat: 34.08572, lng: -118.324569 },
      zoom:               9,
      attributionControl: false,
      dragPan:            true,
      dragRotate:         true,
      scrollZoom:         true,
      doubleClickZoom:    true,
      touchZoomRotate:    true,
      touchPitch:         true,
      keyboard:           true,
      boxZoom:            true,
      bearing:            0,
      pitch:              0,
      maxZoom:            22,
      minZoom:            1,
    });

    this.map.once('idle', () => {
      this.isMapLoaded = true;
      this.addMapControls();
      this.map.resize();

      if (this.points?.length) {
        this.drawRouteWithMarkers(this.points);
      } else {
        this.setLoadingState(false);
      }
    });

    this.map.on('styledata', () => {
      if (this.map?.isStyleLoaded() && this.pendingRouteRender && this.currentRouteCoordinates.length > 1) {
        this.pendingRouteRender = false;
        this.clearRoute();
        this.scheduleRouteRender(this.currentRouteCoordinates);
      }
    });

    this.map.on('error', () => this.setLoadingState(false));
  }

  private addMapControls(): void {
    this.map.addControl(
      new NavigationControl({ showCompass: true, showZoom: true, visualizePitch: true }),
      'top-right'
    );
    this.map.addControl(
      new FullscreenControl({ container: this.mapWrapper.nativeElement }),
      'top-right'
    );
    this.map.addControl(new ScaleControl({ maxWidth: 100, unit: 'metric' }), 'bottom-left');
    this.map.addControl(
      new GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true }),
      'top-right'
    );

    document.addEventListener('fullscreenchange',       this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
  }

  // ─── Fullscreen ───────────────────────────────────────────────────────────

  private handleFullscreenChange(): void {
    const entering = !!(document.fullscreenElement ?? (document as any).webkitFullscreenElement);
    const wrapper: HTMLElement = this.mapWrapper.nativeElement;

    applyFullscreenDimensions(wrapper, entering, this.containerHeight);

    if (entering) {
      this.fullscreenOverlay = mountStyleOverlay(
        wrapper,
        this.currentMapStyle,
        (style) => this.switchMapStyle(style)
      );
    } else {
      this.unmountFullscreenOverlay();
    }

    requestAnimationFrame(() => {
      try { this.map?.resize(); } catch { /* no-op */ }
    });

    this.cdr.detectChanges();
  }

  private unmountFullscreenOverlay(): void {
    this.fullscreenOverlay?.remove();
    this.fullscreenOverlay = null;
  }

  // ─── Map Style ────────────────────────────────────────────────────────────

  public switchMapStyle(styleType: StyleType): void {
    if (this.currentMapStyle === styleType || !this.map || !this.isMapLoaded) return;

    this.currentMapStyle = styleType;
    this.setLoadingState(true);

    requestAnimationFrame(() => {
      this.cancelPendingRetry();

      const savedPoints = [...this.points];
      const savedRoute  = [...this.currentRouteCoordinates];

      this.clearRoute();
      this.clearMarkers();
      this.pendingRouteRender = savedRoute.length > 1;

      try {
        this.map.setStyle(this.mapStyleUrls[styleType]);
      } catch {
        this.setLoadingState(false);
        return;
      }

      if (this.fullscreenOverlay) syncOverlayButtons(this.fullscreenOverlay, styleType);

      const onIdle = () => {
        if (this.styleTimeoutHandle) {
          clearTimeout(this.styleTimeoutHandle);
          this.styleTimeoutHandle = null;
        }
        this.restoreMapContent(savedPoints, savedRoute);
      };

      this.map.once('idle', onIdle);

      this.styleTimeoutHandle = setTimeout(() => {
        this.map.off('idle', onIdle);
        this.restoreMapContent(savedPoints, savedRoute);
      }, 8000);
    });
  }

  private restoreMapContent(points: NextBillionMarker[], route: [number, number][]): void {
    if (points?.length) this.addMarkersForPoints(points);

    if (route.length > 1) {
      this.scheduleRouteRender(route);
    } else {
      this.map.once('idle', () => this.setLoadingState(false));
    }
  }

  // ─── Route Drawing ────────────────────────────────────────────────────────

  private drawRouteWithMarkers(points: NextBillionMarker[]): void {
    if (!points?.length) {
      this.map.once('idle', () => this.setLoadingState(false));
      return;
    }

    this.clearMarkers();
    this.clearRoute();

    if (points.length < 2) {
      this.addMarkersForPoints(points);
      return;
    }

    this.resetCounters();
    this.totalRoutesToProcess = points.length - 1;

    let totalDistance     = 0;
    let totalDuration     = 0;
    let segmentsProcessed = 0;
    const totalSegments   = points.length - 1;
    const routeSegments: Record<number, [number, number][]> = {};

    const onSegmentDone = () => {
      if (++segmentsProcessed !== totalSegments) return;

      const allCoords = Array.from({ length: totalSegments }, (_, i) => routeSegments[i] ?? [])
        .flatMap((seg, i) => i === 0 ? seg : seg.slice(1));

      if (allCoords.length > 1) {
        this.addCompleteRoute(allCoords);
      } else {
        this.setLoadingState(false);
      }

      const totalHours   = Math.floor(totalDuration / 3600);
      const totalMinutes = Math.round((totalDuration % 3600) / 60);
      this.navigationData.emit({
        distanceMiles:   Number(totalDistance.toFixed(2)),
        durationHours:   totalHours,
        durationMinutes: totalMinutes,
      });

      this.routesProcessed = this.totalRoutesToProcess;
      this.checkLoadingComplete();
    };

    this.addMarkersForPoints(points);

    for (let i = 0; i < totalSegments; i++) {
      const segIndex = i;
      this.mapService.getDistance(points[i].latLong, points[i + 1].latLong).subscribe({
        next: (response) => {
          const model = response?.responseModel;
          if (model?.coordinates?.length) {
            routeSegments[segIndex]  = model.coordinates;
            totalDistance           += model.distanceMeters * 0.000621371;
            totalDuration           += model.durationSeconds;
          }
          onSegmentDone();
        },
        error: () => onSegmentDone(),
      });
    }
  }

  private addCompleteRoute(coordinates: [number, number][]): void {
    const valid = filterValidCoords(coordinates);
    if (valid.length < 2) { this.checkLoadingComplete(); return; }
    this.currentRouteCoordinates = [...valid];
    this.scheduleRouteRender(valid);
  }

  private scheduleRouteRender(coordinates: [number, number][], attempt = 0): void {
    this.cancelPendingRetry();
    this.routeRetryHandle = requestAnimationFrame(() => this.attemptRouteRender(coordinates, attempt));
  }

  private attemptRouteRender(coordinates: [number, number][], attempt: number): void {
    if (attempt >= this.MAX_RETRY_ATTEMPTS) {
      this.setLoadingState(false);
      return;
    }

    if (!this.map || !this.isMapLoaded || !this.isMapStyleReady()) {
      setTimeout(() => this.scheduleRouteRender(coordinates, attempt + 1), this.RETRY_DELAY_MS * (attempt + 1));
      return;
    }

    if (this.map.getSource?.('route')) {
      this.clearRoute();
      setTimeout(() => this.scheduleRouteRender(coordinates, attempt + 1), this.RETRY_DELAY_MS);
      return;
    }

    try {
      this.renderRouteOnMap(coordinates);
    } catch {
      setTimeout(() => this.scheduleRouteRender(coordinates, attempt + 1), this.RETRY_DELAY_MS * (attempt + 1));
    }
  }

  private renderRouteOnMap(coordinates: [number, number][]): void {
    if (!this.map) throw new Error('Map is null');

    this.clearRoute();
    const valid = filterValidCoords(coordinates);
    if (valid.length < 2) throw new Error('Not enough valid coordinates');

    this.map.addSource('route', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: valid } },
    });

    this.map.addLayer({
      id: 'route-line-outline', type: 'line', source: 'route',
      paint: { 'line-color': '#ffffff', 'line-width': 10, 'line-opacity': 0.8 },
    });

    this.map.addLayer({
      id: 'route-line', type: 'line', source: 'route',
      paint: { 'line-color': '#007bff', 'line-width': 6, 'line-opacity': 1 },
    });

    const bounds = buildBounds(valid);
    if (isBoundsValid(bounds)) {
      try { this.map.fitBounds(bounds, { padding: 100, duration: 1000 }); } catch { /* no-op */ }
    }

    this.map.once('idle', () => this.setLoadingState(false));
  }

  public reRenderRoute(): void {
    if (this.currentRouteCoordinates.length < 2) return;
    this.clearRoute();
    this.scheduleRouteRender(this.currentRouteCoordinates);
  }

  // ─── Markers ──────────────────────────────────────────────────────────────

  private addMarkersForPoints(points: NextBillionMarker[]): void {
    if (!this.map) { this.setLoadingState(false); return; }

    const bounds = new LngLatBounds();

    points.forEach((point, index) => {
      const coord = parseLatLng(point.latLong);
      bounds.extend(coord);
      const { img, name, address, width } = resolveMarkerAssets(point, index);
      this.markers.push(this.createMarker(coord, name, address, img, width));
    });

    if (points.length && isBoundsValid(bounds)) {
      try { this.map.fitBounds(bounds, { padding: 80 }); }
      catch { this.map.setCenter(parseLatLng(points[0].latLong)); }
    }

    this.markersAdded = true;
    this.checkLoadingComplete();
  }


  private createMarker(
    coordinate: [number, number],
    name: string, address: string, img: string, width: number
  ): Marker {
    const el    = document.createElement('div');
    const imgEl = document.createElement('img');
    imgEl.src            = img ? `assets/img/${img}` : 'assets/img/current-patient-location.png';
    imgEl.style.width    = imgEl.style.height = `${width}px`;
    imgEl.className      = 'bounce-wrapper';
    el.appendChild(imgEl);

    const popup = new Popup({ offset: 25, closeButton: true }).setHTML(
      `<div style="font-size:13px"><strong>${name}</strong><br/>${address}</div>`
    );

    return new Marker({ element: el }).setLngLat(coordinate).setPopup(popup).addTo(this.map);
  }

  private clearMarkers(): void {
    this.markers.forEach(m => { try { m.remove(); } catch { /* already removed */ } });
    this.markers      = [];
    this.markersAdded = false;
  }

  private clearRoute(): void {
    if (!this.map) return;
    ['route-line', 'route-line-outline'].forEach(id => {
      try { if (this.map.getLayer?.(id)) this.map.removeLayer(id); } catch { /* no-op */ }
    });
    try { if (this.map.getSource?.('route')) this.map.removeSource('route'); } catch { /* no-op */ }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private isMapStyleReady(): boolean {
    try {
      return !!(this.map?.isStyleLoaded?.() && this.map.getCanvas?.() && this.map.getContainer?.());
    } catch { return false; }
  }

  private setLoadingState(loading: boolean): void {
    this.isLoading = loading;
    this.cdr.detectChanges();
  }

  private resetCounters(): void {
    this.routesProcessed      = 0;
    this.totalRoutesToProcess = 0;
    this.markersAdded         = false;
  }

  private checkLoadingComplete(): void {
    const hasRoute = this.totalRoutesToProcess > 0;
    if (hasRoute) return;

    this.map.once('idle', () => this.setLoadingState(false));
  }

  private cancelPendingRetry(): void {
    if (this.routeRetryHandle !== null) {
      cancelAnimationFrame(this.routeRetryHandle);
      this.routeRetryHandle = null;
    }
  }

  // ─── Host Listeners ───────────────────────────────────────────────────────

  @HostListener('window:resize')
  onWindowResize(): void {
    this.resize();
  }

  public resize(): void {
    requestAnimationFrame(() => { try { this.map?.resize(); } catch { /* no-op */ } });
  }

  private initResizeObserver(): void {
    if (!('ResizeObserver' in window) || !this.mapWrapper?.nativeElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => { try { this.map?.resize(); } catch { /* no-op */ } });
    });

    this.resizeObserver.observe(this.mapWrapper.nativeElement);
  }
}
